<script>
import {
	authComputed,
	reportsComputed,
	reportsMethods,
	configComputed
} from '@/store/helpers'
import { displayDatetime } from '@/utils/helpers'
import ApiErrorAlert from './ApiErrorAlert.vue'
import _ from 'lodash'
import axiosApiClient from '../api/axiosApiClient'
import moment from 'moment-timezone'

export default {
	name: 'ReportsTable',
	components: { ApiErrorAlert },
	i18nOptions: {
		keyPrefix: 'PORTAL.Reports'
	},
	data() {
		return {
			timer: null,
			count: 0,
			intervalTimes: 10
		}
	},
	computed: {
		...authComputed,
		...reportsComputed,
		...configComputed,
		fields() {
			return [
				{
					key: 'fileName',
					label: this.$t('previous_report_file_name')
				},
				{ key: 'type', label: this.$t('previous_report_report_type') },
				{
					key: 'pickupDate',
					label: this.$t('previous_report_pickup_date')
				},
				{
					key: 'locationId',
					label: this.$t('previous_report_location')
				},
				this.isAdmin && {
					key: 'clientId',
					label: this.$t('previous_report_client')
				},
				{
					key: 'createdAt',
					label: this.$t('previous_report_generated_at')
				},
				{ key: 'fileUrl', label: this.$t('previous_report_download') }
			]
		},
		canApplyFilter() {
			if (this.isPickupDateInvalid) {
				return false
			}

			return Boolean(
				(this.isAdmin ? true : this.filter.clientId) &&
					this.filter.pickupDateTo &&
					this.filter.type
			)
		},
		canReset() {
			return Boolean(
				this.filter.type ||
					this.filter.pickupDateFrom ||
					this.filter.pickupDateTo ||
					this.filter.clientId
			)
		},
		isPickupDateInvalid() {
			// invalid if user input a disabled date
			if (
				this.startDateDisabled(
					null,
					new Date(this.filter.pickupDateFrom)
				) ||
				this.endDateDisabled(null, new Date(this.filter.pickupDateTo))
			) {
				return true
			}
			return false
		},
		locationTimezoneMap() {
			const locationMap = _.groupBy(this.locations, 'id')
			return _.mapValues(locationMap, '0.timezone')
		}
	},
	watch: {
		result(currentValue) {
			const hasPendingPODReportGeneration =
				currentValue.filter(
					report => report.reportStatus === 'GENERATING'
				).length > 0 && this.filter.type === 'POD'
			if (hasPendingPODReportGeneration && !this.timer) {
				this.refreshReportTable()
				this.timer = setInterval(() => {
					this.refreshReportTable()
					this.count++
				}, 180000)
			} else if (!hasPendingPODReportGeneration && this.timer) {
				clearInterval(this.timer)
				this.timer = null
				this.count = 0
			}
		},
		count(currentCount) {
			// setInterval is every 3 mins, after 30 mins, will result count and timer
			if (currentCount === this.intervalTimes) {
				clearInterval(this.timer)
				this.timer = null
				this.count = 0
			}
		}
	},
	beforeMount() {
		this.resetFilter()
		this.getReports({
			pickupDateFrom: new Date().toISOString().slice(0, 10),
			pickupDateTo: new Date().toISOString().slice(0, 10)
		})
	},
	beforeDestroy() {
		clearInterval(this.timer)
		this.count = 0
	},
	methods: {
		...reportsMethods,
		displayDatetime,
		async applyFilter() {
			if (!this.canApplyFilter) {
				return
			}
			clearInterval(this.timer)
			this.timer = null
			this.count = 0
			this.getReports()
		},
		async refreshReportTable() {
			const fetchPODReportStatus = async id => {
				return await axiosApiClient.get(
					`/portal/v1/reports/${id}/status`
				)
			}

			const generatingReports = _.filter(this.result, report => {
				return (
					report.reportStatus === 'GENERATING' &&
					moment
						.duration(moment().diff(moment(report.updatedAt)))
						.asMinutes() < 15
				)
			})

			const reports = await Promise.all(
				generatingReports.map(({ id }) => fetchPODReportStatus(id))
			)

			const filteredReports = reports.filter(
				id => id.reportStatus === 'GENERATED'
			)

			if (filteredReports > 0) {
				const newResult = this.state.result.map(obj =>
					filteredReports.find(o => o.id === obj.id || obj)
				)
				this.updatePODReport(newResult)
			}
		},
		startDateDisabled(_, date) {
			const now = new Date()
			const priorDate = new Date(new Date().setDate(now.getDate() - 60))
			return (
				date > now ||
				date > new Date(this.filter.pickupDateTo) ||
				date < priorDate
			)
		},
		endDateDisabled(_, date) {
			const now = new Date()
			const priorDate = new Date(new Date().setDate(now.getDate() - 60))
			return (
				date < new Date(this.filter.pickupDateFrom) ||
				date > new Date() ||
				date < priorDate
			)
		},
		onPageChange(page) {
			this.getReports({ page })
		},
		toDateString(value) {
			return new Date(value).toISOString().slice(0, 10)
		},
		download(url) {
			window.open(url)
		},
		reset() {
			if (this.canReset) {
				this.resetFilter()
			}
		},
		onStartDateChange(pickupDateFrom) {
			this.updateFilter({ pickupDateFrom })
		},
		onEndDateChange(pickupDateTo) {
			this.updateFilter({ pickupDateTo })
		}
	}
}
</script>

<template>
	<div class="card">
		<div class="card-body">
			<h4 class="header-title mb-3">
				{{ $t('previous_report_heading') }}
			</h4>
			<div>
				<keep-alive>
					<div class="d-flex flex-wrap">
						<div>
							<div class="font-weight-bold mb-1">
								{{ $t('filter_report_type_label') }}
							</div>
							<b-form-select v-model="filter.type">
								<b-form-select-option :value="''">
									{{ $t('filter_report_type_placeholder') }}
								</b-form-select-option>
								<b-form-select-option
									v-for="reportType of reportTypes"
									:key="reportType"
									:value="reportType"
								>
									{{ $t(`report_type.${reportType}`) }}
								</b-form-select-option>
							</b-form-select>
						</div>
						<div>
							<div class="font-weight-bold mb-1">
								{{ $t('previous_report_pickup_date_start') }}
							</div>
							<div class="d-flex">
								<b-input-group class="datepickerContainer mb-2">
									<b-input-group-append>
										<b-form-datepicker
											v-model="filter.pickupDateFrom"
											:date-disabled-fn="
												startDateDisabled
											"
											button-only
											@input="onStartDateChange"
										/>
									</b-input-group-append>
									<b-form-input
										v-model="filter.pickupDateFrom"
										type="text"
										:placeholder="
											$t('filter_report_date_placeholder')
										"
										autocomplete="off"
									/>
								</b-input-group>
							</div>
						</div>
						<div>
							<div class="font-weight-bold mb-1">
								{{ $t('previous_report_pickup_date_end') }}
							</div>
							<div class="d-flex">
								<b-input-group class="datepickerContainer mb-2">
									<b-input-group-append>
										<b-form-datepicker
											v-model="filter.pickupDateTo"
											:date-disabled-fn="endDateDisabled"
											button-only
											@input="onEndDateChange"
										/>
									</b-input-group-append>
									<b-form-input
										v-model="filter.pickupDateTo"
										type="text"
										:placeholder="
											$t('filter_report_date_placeholder')
										"
										autocomplete="off"
									/>
								</b-input-group>
							</div>
						</div>
						<div>
							<div class="font-weight-bold mb-1">
								{{ $t('filter_location_label') }}
							</div>
							<b-form-select v-model="filter.locationId">
								<b-form-select-option :value="''">
									{{ $t('filter_location_placeholder') }}
								</b-form-select-option>
								<b-form-select-option
									v-for="location of locations"
									:key="location.id"
									:value="location.id"
								>
									{{ location.id }}
								</b-form-select-option>
							</b-form-select>
						</div>
						<div
							v-if="
								isAdmin ||
								(currentUser &&
									currentUser.roles.includes('CLT') &&
									clients.length > 1)
							"
						>
							<div class="font-weight-bold mb-1">
								{{ $t('filter_client_label') }}
							</div>
							<b-form-select v-model="filter.clientId">
								<b-form-select-option :value="''">
									{{ $t('filter_client_placeholder') }}
								</b-form-select-option>
								<b-form-select-option
									v-for="client of clients"
									:key="client.id"
									:value="client.id"
								>
									{{ client.id }}
								</b-form-select-option>
							</b-form-select>
						</div>
						<button
							:class="[
								'btn',
								'btn-primary',
								'align-self-center',
								'mt-2',
								canApplyFilter ? '' : 'disabled'
							]"
							@click="applyFilter"
						>
							{{ $t('previous_report_apply_filter') }}
						</button>
						<button
							:class="[
								'btn',
								'btn-secondary',
								'align-self-center',
								'mt-2',
								canReset ? '' : 'disabled'
							]"
							@click="reset"
						>
							{{ $t('previous_report_reset') }}
						</button>
					</div>
				</keep-alive>
				<ApiErrorAlert
					v-if="isPickupDateInvalid"
					error="Start pickup date must be earlier than end pickup date, and within 60 days."
				/>
				<div
					class="d-flex justify-content-between align-items-center border-top pt-3 pb-3"
				>
					<span>
						{{ $t('previous_report_number_of_filtered_reports')
						}}{{ total }}
					</span>
					<b-pagination
						v-model="filter.page"
						class="mb-0"
						:per-page="filter.limit"
						:total-rows="lastPage * filter.limit"
						@change="onPageChange"
					>
						<template #prev-text>Previous</template>
						<template #next-text>Next</template>
					</b-pagination>
				</div>
				<b-table
					responsive="sm"
					sticky-header="450px"
					:items="result"
					:fields="fields"
					:current-page="1"
					:per-page="filter.limit"
				>
					<template #cell(type)="row">
						<span>{{ $t(`report_type.${row.item.type}`) }}</span>
					</template>
					<template #cell(pickupDate)="row">
						<span>
							{{ row.item.pickupDate }}
							{{
								displayDatetime(
									row.item.pickupDate,
									'(UTCZ)',
									locationTimezoneMap[row.item.locationId]
								)
							}}
						</span>
					</template>
					<template #cell(createdAt)="row">
						<span>
							{{
								displayDatetime(
									row.item.createdAt,
									'YYYY-MM-DD HH:mm (UTCZ)',
									locationTimezoneMap[row.item.locationId]
								)
							}}
						</span>
					</template>
					<template #cell(fileUrl)="row">
						<span
							v-if="
								row.item.type !== 'POD' ||
								row.item.reportStatus === 'GENERATED'
							"
							class="mdi mdi-download font-18"
							@click="() => download(row.item.fileUrl)"
						/>
						<span
							v-if="
								row.item.type === 'POD' &&
								row.item.reportStatus === 'GENERATING' &&
								count < intervalTimes
							"
							class="mdi mdi-timer-sand font-18"
							v-b-popover.hover.right="
								$t(`report_generation_in_progress_message`)
							"
						/>
					</template>
				</b-table>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.d-flex > :not(:last-child) {
	margin-right: 1rem;
}

.mdi-download {
	cursor: pointer;
}

button.disabled {
	cursor: not-allowed;
}

.custom-select,
.datepickerContainer input {
	background-color: #eef2f7;
	color: #313a46;
}

.datepickerContainer {
	input {
		border-left: none;
		padding: 0.45rem 0.2rem;
	}
	&::v-deep .dropdown-toggle {
		border-top-left-radius: 0.25rem !important;
		border-bottom-left-radius: 0.25rem !important;
		padding: 0.45rem 0.5rem;
		background-color: #eef2f7;
		color: #6c757d;
		border: 1px solid #dee2e6;
		border-right: none;
		box-shadow: none;
	}
}

.b-table-sticky-header /deep/ .table.b-table > thead > tr > th {
	top: -1px;
}
</style>
